import { zodResolver } from "@hookform/resolvers/zod";
import { type SetupAdminInput, SetupAdminSchema } from "@schemas";
import { Alert, AlertDescription, AlertTitle } from "@veil/ui/components/alert";
import { Button } from "@veil/ui/components/button";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@veil/ui/components/field";
import { Input } from "@veil/ui/components/input";
import { Loader2, Terminal } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

/**
 * Step 1: create the first admin. Public but gated on the one-time setup
 * token printed to `docker logs`. On success the server auto-logs-in (sets
 * the session cookie); we refresh auth + advance.
 */
export function CreateAdminStep({ onNext }: { onNext: () => void }) {
	const { refresh } = useAuth();
	const form = useForm<SetupAdminInput>({
		resolver: zodResolver(SetupAdminSchema),
		defaultValues: { username: "", password: "", setupToken: "" },
		mode: "onSubmit",
	});

	const onSubmit = form.handleSubmit(async (values) => {
		try {
			await unwrap(api.api.system.setup.admin.post(values));
			toast.success("Admin created — you're signed in");
			await refresh();
			onNext();
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Could not create admin",
			);
		}
	});

	const submitting = form.formState.isSubmitting;

	return (
		<form onSubmit={onSubmit} noValidate>
			<Alert className="mb-6">
				<Terminal className="size-4" />
				<AlertTitle>Find your setup token</AlertTitle>
				<AlertDescription>
					A one-time setup token was printed to the container logs on first
					boot. Run{" "}
					<code className="rounded bg-muted px-1 font-mono text-[0.72rem]">
						docker logs veil-cluster-manager
					</code>{" "}
					and look for <strong>SETUP TOKEN</strong> (it's also written to{" "}
					<code className="rounded bg-muted px-1 font-mono text-[0.72rem]">
						/srv/cluster-manager/setup-token
					</code>
					).
				</AlertDescription>
			</Alert>

			<FieldGroup>
				<Field data-invalid={!!form.formState.errors.username}>
					<FieldLabel htmlFor="su-username">Admin username</FieldLabel>
					<Input
						id="su-username"
						autoFocus
						autoComplete="username"
						aria-invalid={!!form.formState.errors.username}
						{...form.register("username")}
					/>
					<FieldError errors={[form.formState.errors.username]} />
				</Field>
				<Field data-invalid={!!form.formState.errors.password}>
					<FieldLabel htmlFor="su-password">Password</FieldLabel>
					<Input
						id="su-password"
						type="password"
						autoComplete="new-password"
						aria-invalid={!!form.formState.errors.password}
						{...form.register("password")}
					/>
					<FieldDescription>At least 8 characters.</FieldDescription>
					<FieldError errors={[form.formState.errors.password]} />
				</Field>
				<Field data-invalid={!!form.formState.errors.setupToken}>
					<FieldLabel htmlFor="su-token">Setup token</FieldLabel>
					<Input
						id="su-token"
						autoComplete="off"
						className="font-mono"
						aria-invalid={!!form.formState.errors.setupToken}
						{...form.register("setupToken")}
					/>
					<FieldError errors={[form.formState.errors.setupToken]} />
				</Field>
				<Button type="submit" disabled={submitting} className="w-full">
					{submitting && (
						<Loader2 data-icon="inline-start" className="animate-spin" />
					)}
					{submitting ? "Creating admin" : "Create admin & continue"}
				</Button>
			</FieldGroup>
		</form>
	);
}
